import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useMemo } from 'react';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import useMemberInfo, {
	B2BMember,
	IFragment,
	ILegalName,
	SubscriptionInfo,
} from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';

import { useAuth } from '@providers/AuthProvider';

import { IPageProps } from '@folx-types/flow-steps';
import { ILatestActiveSubscriptionRxForCurrentMember } from '@folx-types/medications';

import { b2bMember, b2bMemberName } from '@graphql/fragments/b2bMember';
import { legalName, legalNameName } from '@graphql/fragments/legalName';
import { membership, membershipName } from '@graphql/fragments/membership';
import vLatestActiveSubscriptionRxForCurrentMember from '@graphql/queries/vLatestActiveSubscriptionRxForCurrentMember.query.gql';

import MemberHomeStep, { IMemberHomeStepPageContent } from './MemberHomeStep.component';

interface IMemberHomeStepPageProps extends IPageProps {}

const MemberHomeStepPage: FC<IMemberHomeStepPageProps> = ({ path }) => {
	const { loggedIn } = useAuth();

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "member-home-step" }) {
				page_header
				billing_header
				billing_description
				appointments_header
				appointments_description
				labs_header
				labs_description
				innerspace_header
				innerspace_description
				library_header
				library_description
				help_header
				help_description
				get_care_header
				messages_header
				member_home_toast {
					message
					link
					show_toast
				}
			}
		}
	`);

	const {
		data: rxData,
		loading: rxLoading,
		error: rxError,
	} = useQuery<ILatestActiveSubscriptionRxForCurrentMember>(
		vLatestActiveSubscriptionRxForCurrentMember,
		{ skip: !loggedIn }
	);
	const activePrescriptions = rxData?.vLatestActiveSubscriptionRxForCurrentMember?.nodes ?? [];

	const fragments: IFragment[] = useMemo(
		() => [
			{ name: membershipName, node: membership },
			{ name: legalNameName, node: legalName },
			{ name: b2bMemberName, node: b2bMember },
		],
		[]
	);
	const { loading: memberInfoLoading, memberInfo } = useMemberInfo<
		SubscriptionInfo & ILegalName & B2BMember
	>(fragments);

	const refinedContent: IMemberHomeStepPageContent = keysToCamel(
		pageContent
	) as IMemberHomeStepPageContent;

	const loading = memberInfoLoading || rxLoading;

	return (
		<LoadingWrapper loading={loading}>
			<MemberHomeStep
				pageContent={refinedContent}
				path={path}
				activePrescriptions={activePrescriptions}
				member={memberInfo}
			/>
		</LoadingWrapper>
	);
};

export default MemberHomeStepPage;
